/**
 * Canonical "return to this path after auth" guard (USP-41).
 *
 * ONE definition of a safe post-login destination, shared by apps/web and the
 * apps/api BFF so the SPA and the server can never disagree about what counts
 * as same-origin. Returns the candidate unchanged when it is a same-origin
 * relative path, or `null` when it is not — the caller supplies its own safe
 * default (never trust the client; the BFF re-validates independently).
 *
 * Why a plain leading-character check is NOT enough: browsers strip ASCII
 * control characters (tab, CR, LF, and the rest of the C0 range + DEL) BEFORE
 * they parse a URL. So `"/\t/evil.example.com"` passes a naive
 * `startsWith('/') && s[1] !== '/'` check, yet the browser first removes the
 * tab, leaving `"//evil.example.com"` — a protocol-relative URL pointing
 * off-origin. That was the live open redirect at the BFF's post-login
 * `reply.redirect(...)`.
 *
 * Defense is belt-and-suspenders:
 *  1. Reject any ASCII control char (0x00–0x1F, 0x7F) outright — nothing a
 *     legitimate relative path needs, and the exact class browsers pre-strip.
 *  2. Reject the character-prefix tricks (`//`, `/\`, leading `\`).
 *  3. Resolve the candidate against a fixed, unreachable base origin with the
 *     WHATWG URL parser and assert the result is STILL that origin and still a
 *     single-leading-slash path — this catches anything that normalizes to a
 *     different origin regardless of how it was spelled.
 */

// A private, syntactically-valid, non-routable base. Any candidate that
// resolves to a different origin than this is, by definition, not same-origin.
const BASE_ORIGIN = 'https://safe-return-to.invalid'

// C0 control chars (0x00–0x1F) and DEL (0x7F). Includes tab (0x09), LF (0x0A),
// CR (0x0D) — the values browsers strip before parsing.
// biome-ignore lint/suspicious/noControlCharactersInRegex: matching pre-stripped control chars is the intended behavior
const CONTROL_CHARS = /[\u0000-\u001F\u007F]/

export function safeReturnTo(candidate: unknown): string | null {
  if (typeof candidate !== 'string' || candidate.length === 0) {
    return null
  }
  if (CONTROL_CHARS.test(candidate)) {
    return null
  }
  // Must be a leading-single-slash path: not "//host", not "/\host", not "\".
  if (candidate[0] !== '/' || candidate[1] === '/' || candidate[1] === '\\') {
    return null
  }
  try {
    const url = new URL(candidate, BASE_ORIGIN)
    if (url.origin !== BASE_ORIGIN) {
      return null
    }
    // Re-derive the resolved path and confirm it did not sprout an authority
    // (a second leading slash) during normalization.
    const resolved = `${url.pathname}${url.search}${url.hash}`
    if (resolved[0] !== '/' || resolved[1] === '/') {
      return null
    }
  } catch {
    return null
  }
  return candidate
}
